/**
 * @fileoverview Main application entry point
 * @module App
 */

import { ReactElement } from 'react'
import { BrowserRouter, useLocation } from 'react-router-dom'
import { useActiveOrg } from '@/features/orgs/context/ActiveOrgProvider'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'sonner'
import { AppRouter } from './router/AppRouter'
import { ThemeProvider } from '@/shared/providers'
import { FilterMenuSlotProvider } from '@/shared/contexts/FilterMenuSlotContext'
import { FilterProvider } from '@/shared/providers/FilterProvider'
import StagewiseToolbarWrapper from '@/shared/components/StagewiseToolbarWrapper'
import { AppWrapper } from '@/shared/components/common/PageMeta'
import { AppSidebar } from '@/components/app-sidebar'
import { FlyingMenu } from '@/shared/components/filters/FlyingMenu'
import { FilterSheet } from '@/shared/components/filters/FilterSheet'
import { MobileNavTrigger } from '@/shared/components/MobileNavTrigger'
import { MobileNavProvider } from '@/shared/contexts/MobileNavContext'
import { FilterSheetProvider } from '@/shared/contexts/FilterSheetContext'
import { authService } from '@/lib/authService'
import { useOrderNotifications } from '@/features/daily-summary/hooks/useOrderNotifications'
import { ActiveOrgProvider } from '@/features/orgs/context/ActiveOrgProvider'
import { OrgSettingsProvider } from '@/features/settings/context/OrgSettingsProvider'
import './index.css'

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
})

/**
 * Root application component with providers
 *
 * @component
 * @example
 * ```tsx
 * <App />
 * ```
 */
export function App(): ReactElement {
  return (
    <QueryClientProvider client={queryClient}>
      <AppWrapper>
        <ThemeProvider>
          <BrowserRouter>
            <ActiveOrgProvider>
              <OrgSettingsProvider>
                <FilterProvider>
                  <AppChrome />
                </FilterProvider>
              </OrgSettingsProvider>
            </ActiveOrgProvider>
          </BrowserRouter>
        </ThemeProvider>
      </AppWrapper>
    </QueryClientProvider>
  )
}

export default App

/**
 * Internal app chrome that renders global UI (sidebar, flying menu) conditionally.
 * Hidden on public routes like landing page and when unauthenticated.
 */
function AppChrome(): ReactElement {
  const location = useLocation()
  const isAuthed = authService.isAuthenticated()

  // Only show app chrome (sidebar, menus) on dashboard routes
  const showChrome = isAuthed && location.pathname.startsWith('/dashboard')
  const { preferredLanguageMissing } = useActiveOrg()

  // Order arrival toasts (scoped to /dashboard and /dashboard/performance internally)
  useOrderNotifications()
  // Only mount the FlyingMenu on a whitelisted set of pages
  const flyingMenuAllowedPaths = new Set<string>(['/dashboard', '/dashboard/performance', '/dashboard/orders', '/dashboard/ad-spends'])
  const flyingMenuAllowedPrefixes = ['/dashboard/product/']
  const showFlyingMenu = showChrome && (
    flyingMenuAllowedPaths.has(location.pathname) ||
    flyingMenuAllowedPrefixes.some(prefix => location.pathname.startsWith(prefix))
  )
  const flyingMenuWidths: Record<string, string> = {
    '/dashboard/orders': 'max-w-screen-2xl',
    '/dashboard/ad-spends': 'max-w-screen-2xl',
  }
  const defaultMenuWidth = flyingMenuWidths[location.pathname] ?? 'max-w-screen-xl'

  return (
    <div className="min-h-screen">
      {/* First-run banner for language selection */}
      {showChrome && preferredLanguageMissing && (
        <div className="w-full bg-amber-100 text-amber-900 px-4 py-2 text-sm border-b border-amber-300">
          <div className="max-w-screen-xl mx-auto flex items-center justify-between gap-4">
            <span>Choose your language to complete setup. Use the User menu → Language.</span>
            <button
              type="button"
              className="px-3 py-1 rounded-md bg-amber-200 hover:bg-amber-300"
              onClick={() => document.getElementById('user-menu-trigger')?.click()}
            >
              Open language menu
            </button>
          </div>
        </div>
      )}
      <FilterMenuSlotProvider defaultMaxWidthClass={defaultMenuWidth}>
        <FilterSheetProvider>
          <MobileNavProvider>
            {showChrome && <AppSidebar />}
            <div className={
              showChrome ? 'lg:ml-[var(--sidebar-rail)] lg:w-[calc(100%-var(--sidebar-rail))]' : ''
            }>
              {showFlyingMenu && <FlyingMenu />}
              {/* Mobile/Tablet header with hamburger for pages without FlyingMenu */}
              {showChrome && !showFlyingMenu && (
                <div className="sticky top-0 z-50 lg:hidden">
                  <div className="px-4 mt-2">
                    <div className="h-[54px] flex items-stretch">
                      <MobileNavTrigger />
                    </div>
                  </div>
                </div>
              )}
              <AppRouter />
            </div>
            {/* Mobile/tablet filter sheet */}
            {showFlyingMenu && <FilterSheet />}
          </MobileNavProvider>
        </FilterSheetProvider>
      </FilterMenuSlotProvider>
      <Toaster
        position="top-right"
        richColors
        closeButton
        offset="88px"
        toastOptions={{
          classNames: {
            success: '!bg-[var(--glass-surface-dark)] !border-border/50 !text-foreground',
            closeButton: '!bg-transparent !border-border/50 !text-muted-foreground hover:!text-foreground',
          },
        }}
      />
      <StagewiseToolbarWrapper />
    </div>
  )
}
